import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, combineLatest, forkJoin } from 'rxjs';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { MarkdownEditorComponent } from '../../shared/markdown-editor/markdown-editor.component';

import { KnowledgeBaseItemService } from '../../../services/knowledge-base-item.service';
import { IntentService } from '../../../services/intent.service';
import { DepartmentService } from '../../../services/department.service';
import { TextToSpeechService } from '../../../services/text-to-speech.service';
import { ServiceAreaService } from '../../../services/service-area.service';
import { AutoFocusDirective } from '../../../directives/auto-focus.directive';
import { ReadOnlyIfNoGroupPermissionDirectiveDirective } from '../../../directives/read-only-if-no-group-permission-directive.directive';
import { OrganizationService } from '../../../services/organization.service';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-knowledge-base-item-form',
  imports: [FormsModule, CommonModule, MarkdownEditorComponent, MultiSelectModule, SelectModule, RouterModule, AutoFocusDirective, ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './knowledge-base-item-form.component.html',
  styleUrl: './knowledge-base-item-form.component.scss'
})
export class KnowledgeBaseItemFormComponent implements OnInit, AfterViewInit {

  @ViewChild('audioPlayer', { static: true }) audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('kbForm') kbForm!: NgForm;
  @ViewChild('descriptionInput') descriptionInput!: ElementRef<HTMLInputElement>;

  //#region Component State Variables
  kbItemId: string | null | undefined; // Stores the ID of the Knowledge Base item (for edit mode)
  serviceArea: | null = null; // Holds the name of the service area
  isEditMode = false; // Determines whether the form is in edit mode

  departments: any[] = []; // Stores available departments
  kbItems: any[] = []; // Stores available KB Items
  availableWebIntents: any[] = [];    // Available intents for web selection
  availableVoiceSMSIntents: any[] = []; // Available intents for voice/sms selection

  isLoadingPrimary = true; // For primary kbItem load
  isLoadingSecondary = true; // For secondary data (intents, departments, kbItems) 

  webQuestions$ = new BehaviorSubject<string[]>([]); // Holds the selected web questions
  voiceSMSQuestions$ = new BehaviorSubject<string[]>([]); // Holds the selected voice/sms questions

  selectedRefinementIndex: number | null = null; // Tracks the index of the refinement question for deletion confirmation
  refinementeDeleting: string | undefined;
  //#endregion

  kbItem: any = {
    id: undefined,
    description: '',
    webQuestions: [],
    voiceSMSQuestions: [],
    webAnswer: '',
    voiceAnswer: '',
    smsAnswer: '',
    autoEscalate: false,
    escalationDepartmentId: null,
    webRefinementQuestions: [],
    voiceSmsRefinementQuestions: []
  };
  
  hasPermission: boolean = true;

  constructor(
    private kbItemService: KnowledgeBaseItemService,
    private intentService: IntentService,
    private organizationService: OrganizationService,
    private permissionService: PermissionService,
    private departmentService: DepartmentService,
    private serviceAreaService: ServiceAreaService,
    private route: ActivatedRoute,
    private router: Router,
    private textToSpeechService: TextToSpeechService,
    private modalService: NgbModal
  ) { }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.descriptionInput.nativeElement.focus({ preventScroll: true });      
    }, 1000);
  }

  ngOnInit(): void {
    this.kbItemId = this.route.snapshot.paramMap.get('id');
    const serviceAreaId = this.route.snapshot.paramMap.get('serviceAreaId');
    this.isEditMode = !!this.kbItemId;

   
    if (this.isEditMode) {
      this.loadPrimaryEditData(this.kbItemId!);
    } else {
      this.loadPrimaryCreateData(serviceAreaId);
    }

    const organizationId = this.organizationService.currentOrganizationId;
    this.hasPermission = (organizationId && this.permissionService.hasElevatedPermission(organizationId)) as boolean;
  }

  private loadPrimaryCreateData(serviceAreaId: string | null): void {
    this.kbItem.serviceAreaId = serviceAreaId;
    this.serviceAreaService.getServiceNameById(serviceAreaId || '').subscribe({
      next: (serviceArea) => {
        this.serviceArea = serviceArea.name;
        this.isLoadingPrimary = false;
        this.loadSecondaryData(null); // Load secondary data after primary
      },
      error: (error) => {
        console.error('Error loading service area:', error);
        this.isLoadingPrimary = false;
      }
    });
  }

  private loadPrimaryEditData(kbItemId: string): void {
    forkJoin({
      kbItem: this.kbItemService.getKnowledgeBaseItem(kbItemId)
    }).subscribe({
      next: ({ kbItem }) => {
        this.kbItem = kbItem;
        this.webQuestions$.next(kbItem.webQuestions?.map((intent: any) => intent.id) || []);
        this.voiceSMSQuestions$.next(kbItem.voiceSMSQuestions?.map((intent: any) => intent.id) || []);
        this.isLoadingPrimary = false;
        this.loadSecondaryData(kbItemId); // Load secondary data after primary
      },
      error: (error) => {
        console.error('Error loading primary edit data:', error);
        this.isLoadingPrimary = false;
      }
    });
  }

  private loadSecondaryData(kbItemId: string | null): void {
    this.isLoadingSecondary = true; // Keep multiselects in loading state
    forkJoin({
      departments: this.departmentService.getDepartments(),
      kbItems: this.kbItemService.getKnowledgeBaseItemsWithoutSmalltalk(),
      webIntents: this.intentService.getIntentsForWeb(kbItemId),
      voiceSMSIntents: this.intentService.getIntentsForVoiceSMS(kbItemId)
    }).subscribe({
      next: ({ departments, kbItems, webIntents, voiceSMSIntents }) => {
        this.departments = departments;
        this.kbItems = kbItems.map(item => ({
          id: item.id,
          description: item.description,
          answer: item.webAnswer
        }));
        this.availableWebIntents = webIntents;
        this.availableVoiceSMSIntents = voiceSMSIntents;
        this.isLoadingSecondary = false;
      },
      error: (error) => {
        console.error('Error loading secondary data:', error);
        this.isLoadingSecondary = false;
      }
    });
  }



  private loadCreateModeData(serviceAreaId: string | null): void {
    this.kbItem.serviceAreaId = serviceAreaId;

    // Combine all initial data fetches
    forkJoin({
      departments: this.departmentService.getDepartments(),
      kbItems: this.kbItemService.getAllKnowledgeBaseItems(),
      webIntents: this.intentService.getIntentsForWeb(null),
      voiceSMSIntents: this.intentService.getIntentsForVoiceSMS(null),
      serviceArea: this.serviceAreaService.getServiceNameById(serviceAreaId || '')
    }).subscribe({
      next: ({ departments, kbItems, webIntents, voiceSMSIntents }) => {
        this.departments = departments;
        this.kbItems = kbItems.map(item => ({
          id: item.id,
          description: item.description,
          answer: item.webAnswer
        }));
        this.availableWebIntents = webIntents;
        this.availableVoiceSMSIntents = voiceSMSIntents;
      },
      error: (error) => {
        console.error('Error loading create mode data:', error);
      }
    });
  }
  
  private loadEditModeData(kbItemId: string): void {
    // Combine all edit mode data fetches
    forkJoin({
      kbItem: this.kbItemService.getKnowledgeBaseItem(kbItemId),
      departments: this.departmentService.getDepartments(),
      kbItems: this.kbItemService.getAllKnowledgeBaseItems(),
      webIntents: this.intentService.getIntentsForWeb(kbItemId),
      voiceSMSIntents: this.intentService.getIntentsForVoiceSMS(kbItemId)
    }).subscribe({
      next: ({ kbItem, departments, kbItems, webIntents, voiceSMSIntents }) => {
        this.kbItem = kbItem;
        this.departments = departments;
        this.kbItems = kbItems.map(item => ({
          id: item.id,
          description: item.description,
          answer: item.webAnswer
        }));
        this.availableWebIntents = webIntents;
        this.availableVoiceSMSIntents = voiceSMSIntents;

        // Initialize BehaviorSubjects with existing selections
        this.webQuestions$.next(kbItem.webQuestions?.map((intent: any) => intent.id) || []);
        this.voiceSMSQuestions$.next(kbItem.voiceSMSQuestions?.map((intent: any) => intent.id) || []);
      },
      error: (error) => {
        console.error('Error loading edit mode data:', error);
      }
    });
  }


   // Check if questions and answers are valid
  areQuestionsAndAnswersValid(): boolean {
    const hasWebQuestions = this.webQuestions$.value.length > 0;
    const hasVoiceSMSQuestions = this.voiceSMSQuestions$.value.length > 0;
    
    // Web validation: If web questions exist, webAnswer must not be empty
    if (hasWebQuestions && !this.kbItem.webAnswer.trim()) {
      return false;
    }

    // Voice/SMS validation: If Voice/SMS questions exist, at least one of voiceAnswer or smsAnswer must not be empty
    if (hasVoiceSMSQuestions && !this.kbItem.voiceAnswer.trim() && !this.kbItem.smsAnswer.trim()) {
      return false;
    }

    // If webAnswer is provided, ensure there are web questions
    if( this.kbItem?.webAnswer?.trim() && !hasWebQuestions  ){
      return false;
    }

    // If voiceAnswer or smsAnswer is provided, ensure there are voice/SMS questions
    if( !hasVoiceSMSQuestions   &&  ( this.kbItem?.voiceAnswer?.trim() ||  this.kbItem?.smsAnswer?.trim())  )      {
      return false;
    }

    return true;
  }

  // Update kbItem when selections change
  updateKbItemQuestions(): void {
    this.kbItem.webQuestions = this.webQuestions$.value
      ? this.availableWebIntents.filter(i => this.webQuestions$.value.includes(i.id))
      : [];

    this.kbItem.voiceSMSQuestions = this.voiceSMSQuestions$.value
      ? this.availableVoiceSMSIntents.filter(i => this.voiceSMSQuestions$.value.includes(i.id))
      : [];
  }

  //#endregion

  //#region Refinement Questions Management
  /**
   * Adds a new Refinement Question entry to the list
   */
  addRefinementQuestion(refinementQuestions: any[]): void {
    // Prevent adding an empty refinement question
    if (refinementQuestions.some((q: any) => !q.targetKnowledgeBaseItem)) {
      alert('You must select a KB Item before adding another Refinement Question.');
      return;
    }

    // Prevent duplicate selection
    const selectedIds = refinementQuestions.map((q: any) => q.targetKnowledgeBaseItem?.id);
    if (selectedIds.includes(null)) return;

    // Add a new empty refinement question
    refinementQuestions.push({
      id: crypto.randomUUID(),
      displayText: '',
      targetKnowledgeBaseItem: {}
    });
  }

  /**
  * Opens a confirmation modal before deleting a refinement question
  * @param index The index of the refinement question to be deleted
  * @param content The modal reference
  */
  confirmDeleteRefinementQuestion(index: number, content: any, type: string): void {
    this.selectedRefinementIndex = index;
    this.refinementeDeleting = type;
    this.modalService.open(content, { centered: true });
  }

  /**
   * Deletes the selected refinement question
   * @param modal The modal instance to be closed
   */
  deleteRefinementQuestion(modal: any): void {
    if (this.selectedRefinementIndex !== null) {

      if (this.refinementeDeleting === 'phone') {
        this.kbItem.voiceSmsRefinementQuestions.splice(this.selectedRefinementIndex, 1);
      }

      if (this.refinementeDeleting === 'web') {
        this.kbItem.webRefinementQuestions.splice(this.selectedRefinementIndex, 1);
      }

      this.selectedRefinementIndex = null;
    }

    modal.close();
  }
  //#endregion

  //#region Form Submission & Validation
  /**
   * Saves or updates a Knowledge Base Item
   */
  saveKbItem(): void {
    // Prevent submission if the form is invalid
    if (this.kbForm.invalid || !this.areQuestionsAndAnswersValid()) {
      return;
    }

    // Ensure kbItem reflects current selections before saving
    this.updateKbItemQuestions();

    if (this.isEditMode) {
      this.kbItemService.updateKnowledgeBaseItem(this.kbItem.id, this.kbItem)
        .subscribe({
          next: () => this.cancel(),
          error: (error) => console.error('Error updating KnowledgeBase Item:', error)
        });
    }
    else {
      this.kbItem.id = null;

      this.kbItemService.addKnowledgeBaseItem(this.kbItem)
        .subscribe({
          next: () => this.cancel(),
          error: (error) => console.error('Error adding  KnowledgeBase Item:', error)
        });
    }
  }

  /**
  * Navigates back to the list without saving
  */
  cancel(): void {
    this.router.navigate(['knowledge-base-items', this.kbItem.serviceAreaId]);
  }
  //#endregion

  //#region Text-to-Speech Feature
  /**
   * Plays the out-of-hours message using a Text-to-Speech service
   * @param text The text to be converted to speech
   */
  playOutOfHoursMessage(text: string) {
    console.log('Playing Out of Hours Message');

    if (!text) {
      alert('Please enter an Out of Hours message.');
      return;
    }

    this.textToSpeechService.convertTextToSpeech(text).subscribe((audioBlob) => {
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('Audio URL generated:', audioUrl);
      this.audioPlayer.nativeElement.src = audioUrl;
      this.audioPlayer.nativeElement.play();
    });
  }
  //#endregion

}
